import {
  Component,
  Input,
  NgZone,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
} from "@angular/core";
import {
  AnalyticsService,
  AuthorizationService,
  ExportService,
  LoggingService,
  ManualOpenDataPoint,
  ManualsOpenedData,
  ToastrService,
} from "app/shared";
import {
  ChartDefinitions,
  ChartDefinition,
} from "../../../chartDefinitions.config";
import { ManualsOpenedConfig } from "app/shared/model/charts/user-analytic-configurations.model";
import { BaseChartLogic } from "app/analytics/BaseChartLogic";

@Component({
  selector: "manuals-opened-chart",
  templateUrl: "../../common-chart-template.component.html",
  standalone: false,
})
export class ManualsOpenedChartComponent
  extends BaseChartLogic<ManualsOpenedConfig, ManualsOpenedData>
  implements OnInit, OnDestroy
{
  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() showTitle: boolean = true;
  @ViewChild("chartCanvas") chartCanvas!: ElementRef<HTMLCanvasElement>;

  constructor(
    _analyticsService: AnalyticsService,
    _authService: AuthorizationService,
    _exportService: ExportService,
    _toastrService: ToastrService,
    _loggingService: LoggingService,
    _ngZone: NgZone
  ) {
    super(
      _analyticsService,
      _authService,
      _exportService,
      _toastrService,
      _loggingService,
      ManualsOpenedConfig,
      _ngZone
    );

    this.handleDataSubscription(this.analyticsService.manualsOpendedDataChange);
  }

  getCustomLabel(dataPoint: ManualOpenDataPoint, label: string): any {
    const result: string[] = [];
    const result_asTable: any[] = [];

    if (label === "Dates Opened") {
      for (const date of dataPoint.DatesOpened) {
        const row = {};
        row["Date"] = new Date(date.DateOpened)
          .toLocaleDateString()
          .replace(",", "-");
        row["Time"] = new Date(date.DateOpened).toLocaleTimeString();
        row["First Name"] = date.User.FirstName;
        row["Last Name"] = date.User.LastName;
        row["User Name"] = date.User.UserName;
        result_asTable.push(row);
      }
    }

    if (label === "Pages Viewed") {
      let totalPageViews = 0;
      const customLabelSelection = this.config?.customLabelSelection[
        "Pages Viewed"
      ]?.length
        ? this.config.customLabelSelection["Pages Viewed"]
        : this.chartDetails?.customLabels["Pages Viewed"];

      if (customLabelSelection && customLabelSelection.length === 1) {
        for (const [page, count] of Object.entries(
          dataPoint.PagesViewed || {}
        )) {
          if (customLabelSelection.includes("Page")) {
            result.push(page);
          } else if (customLabelSelection.includes("Total")) {
            totalPageViews += count;
          }
        }

        if (totalPageViews > 0) {
          result.push(`Total Page Views: ${totalPageViews}`);
        }
      } else if (customLabelSelection && customLabelSelection.length > 1) {
        //build as secondary table, there are only two options
        for (const [page, count] of Object.entries(
          dataPoint.PagesViewed || {}
        )) {
          const row = {};
          row["Page"] = page;
          row["Count"] = count;
          result_asTable.push(row);
        }
      }
    }

    if (label === "Search Terms Used") {
      let totalSearches = 0;
      const customLabelSelection = this.config?.customLabelSelection[
        "Search Terms Used"
      ]?.length
        ? this.config.customLabelSelection["Search Terms Used"]
        : this.chartDetails?.customLabels["Search Terms Used"];

      if (customLabelSelection && customLabelSelection.length === 1) {
        for (const [term, count] of Object.entries(
          dataPoint.SearchTermsUsed || {}
        )) {
          if (customLabelSelection.includes("Search Term")) {
            result.push(`"${term}"`);
          } else if (customLabelSelection.includes("Total")) {
            totalSearches += count;
          }
        }
        if (totalSearches > 0) {
          result.push(`Total Searches: ${totalSearches}`);
        }
      } else if (customLabelSelection && customLabelSelection.length > 1) {
        //build as secondary table, there are only two options
        for (const [page, count] of Object.entries(
          dataPoint.SearchTermsUsed || {}
        )) {
          const row = {};
          row["Search Term"] = page;
          row["Count"] = count;
          result_asTable.push(row);
        }
      }
    }

    return result_asTable.length > 0
      ? result_asTable
      : result.length > 0
      ? result.join("; ")
      : "No data available";
  }

  dataPointKey: string = "Total";
  protected getDataPointDisplayValue(dataPoint: ManualOpenDataPoint): any {
    return dataPoint.Total;
  }

  buildFilterSettings(): void {
    this.addPeriodtoFilterSettings();
    this.addStartEndDatesToFilterSettings(this.startDate, this.endDate);
    this.filterSettings.FilterByMinimum = this.config?.minOpened || null;
    this.filterSettings.LimitResults = this.config?.top || null;
  }

  refreshData(): void {
    this.analyticsService.getManualsOpenedData(this.filterSettings);
  }

  ngOnInit(): void {
    super.init();
  }

  ngOnDestroy(): void {
    super.cleanup();
  }
}
